import { Card, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import MDBox from "components/MDBox";
import BnsfFuelRateImport from "./BnsfFuelRateImport";
import PayrollDryRun from "./PayrollDryRun";
import BnsfRoadDisputes from "./BnsfRoadDisputes";
import { useFinancePermissions } from "finance/permissions";

/**
 * Finance &gt; Ad Hoc Processes. A home for one-off operational tools that finance runs by hand,
 * one tab per process. Add a process by adding a row to `processes` below -- the tab, its a11y
 * ids and its panel are all driven off that array.
 */
const AdHocProcesses = () => {
  // Road Disputes rides the existing AccessFinance permission, the same one the Finance menu
  // and the Shop Invoice Submission card use.
  const { canAccessFinance } = useFinancePermissions();
  const [activeTab, setActiveTab] = useState(0);

  // Filtered array rather than conditional <Tab> children: a tab the user cannot see must not
  // shift the indices the panels are keyed on, or hiding one would show the wrong process.
  const processes = [
    { id: "bnsf-fuel-rates", label: "BNSF Fuel Rates", panel: <BnsfFuelRateImport /> },
    { id: "payroll-dry-run", label: "Payroll Dry Run", panel: <PayrollDryRun /> },
    ...(canAccessFinance
      ? [{ id: "bnsf-road-disputes", label: "BNSF Road Disputes", panel: <BnsfRoadDisputes embedded /> }]
      : []),
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <MDBox p={3}>
      <Typography variant="h5" gutterBottom>
        Ad Hoc Processes
      </Typography>

      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          aria-label="ad hoc processes"
          variant="scrollable"
          scrollButtons="auto"
        >
          {processes.map((process, index) => (
            <Tab
              key={process.id}
              className={activeTab === index ? "tab tab-active" : "tab"}
              label={process.label}
              id={`${process.id}-tab`}
              aria-controls={`${process.id}-tab-panel`}
            />
          ))}
        </Tabs>
      </Card>

      {/* Panels stay mounted and are hidden, not unmounted. Each of these screens holds work in
          progress -- a previewed disputes workbook, a dry-run result -- and unmounting on a tab
          change would throw it away silently, which reads as the upload having failed. */}
      {processes.map((process, index) => (
        <div
          key={process.id}
          id={`${process.id}-tab-panel`}
          role="tabpanel"
          aria-labelledby={`${process.id}-tab`}
          hidden={activeTab !== index}
        >
          {process.panel}
        </div>
      ))}
    </MDBox>
  );
};

export default AdHocProcesses;
